import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FieldArrayWithId } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import type { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';
import type { Question } from '@aphp-ema/common/questionnaire';
import { QuestionHOC } from '../QuestionHOC';
import { ConditionsModal } from '../conditions/QuestionConditionsModal';
import { QuestionHeader } from './QuestionHeader';
import { useQuestionOperationsContext } from '@/features/questionnaires/hooks/useQuestionOperationsContext';
import { useQuestionConditionStats } from '@/features/questionnaires/hooks/questionnaire/useQuestionConditionStats';

type QuestionItemProps = {
  field: FieldArrayWithId<QuestionnaireFormData, 'questions', '_id'>;
  index: number;
  questions: Question[];
};

export const QuestionItem: React.FC<QuestionItemProps> = ({
  field,
  index,
  questions,
}) => {
  const [conditionsModalOpen, setConditionsModalOpen] = useState(false);
  const { control, getFieldState } = useFormContext<QuestionnaireFormData>();
  const { onRemove } = useQuestionOperationsContext();

  const displayConditions = useWatch({
    control,
    name: `questions.${index}.displayConditions`,
  });

  const allStats = useQuestionConditionStats({ currentQuestions: questions });
  const questionStats = allStats.get(field.id);

  const hasError = !!getFieldState(`questions.${index}`).error;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    border: hasError ? '2px solid' : '1px solid #e0e0e0',
    borderColor: hasError ? '#f04549' : '#e0e0e0',
    borderRadius: 3,
    backgroundColor: isDragging ? 'action.hover' : 'background.paper',
    cursor: isDragging ? 'grabbing' : 'default',
  };

  return (
    <Box
      ref={setNodeRef}
      data-question-index={index}
      style={style}
      sx={{
        mb: 2,
        p: 2,
      }}
    >
      <QuestionHeader
        index={index}
        onConditionsClick={() => setConditionsModalOpen(true)}
        onDelete={() => onRemove?.(index)}
        dragHandleProps={{ ...attributes, ...listeners }}
        conditionsCount={displayConditions?.conditions?.length ?? 0}
        questionStats={questionStats}
      />

      <QuestionHOC questionIndex={index} />

      <ConditionsModal
        open={conditionsModalOpen}
        onClose={() => setConditionsModalOpen(false)}
        questionIndex={index}
        questionStats={questionStats}
        allQuestions={questions}
        questions={questions}
      />
    </Box>
  );
};
